import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';

import { AxpoTypographyComponent } from '../../../core/axpo-typography/axpo-typography.component';
import { TileService } from '../../../pages/incident-detail/tile.service';
import { DataFragments, DetailsService } from '../../services/details.service';
import { IncidentService } from '../../services/incident.service';
import { TenantService } from '../../services/tenant.service';
import { isoDateToTime } from '../../utils/date';
import { ITileMode } from '../../utils/tileStates';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-view-details',
  templateUrl: './view-details.component.html',
  imports: [TranslocoPipe, AxpoTypographyComponent, DatePipe],
})
export class ViewDetailsComponent {
  incidentService = inject(IncidentService);
  tenantService = inject(TenantService);
  detailsService = inject(DetailsService);

  tileService = inject(TileService);
  activatedRoute = inject(ActivatedRoute);
  categoryName = signal<string | undefined>(undefined);
  creatorName = signal<string | undefined>(undefined);
  description = signal<string | undefined>(undefined);
  faultDate = signal<Date | undefined>(undefined);
  fixedDate = signal<Date | undefined>(undefined);
  timeStamp = signal<string | undefined>(undefined);
  fixedTimeStamp = signal<string | undefined>(undefined);
  faultEffectName = signal<string | undefined>(undefined);
  impactName = signal<string | undefined>(undefined);
  incidentStateName = signal<string | undefined>(undefined);
  incidentTitle = signal<string | undefined>(undefined);
  hasInterruptions = signal<string>('no');
  faultEffectId = signal<string | undefined>(undefined);
  accessCode = computed(() => {
    return this.incidentService.incidentDetails()?.accessCode;
  });
  tenantId = computed(() => {
    const tenantid =
      this.tenantService.tenantId() ||
      (this.activatedRoute.snapshot.paramMap.get('tenantId') ?? undefined);
    return tenantid;
  });
  mode = input<ITileMode>('edit');

  _ = effect(() => {
    const details = this.incidentService.incidentDetails();
    untracked(() => {
      this.categoryName.set(details?.incidentCategoryName ?? undefined);
      this.creatorName.set(details?.incidentCreatorName ?? undefined);
      this.description.set(details?.incidentDescription ?? undefined);
      this.faultDate.set(
        details?.incidentFaultTimeStamp ? new Date(details.incidentFaultTimeStamp) : undefined,
      );
      this.timeStamp.set(
        details?.incidentFaultTimeStamp ? isoDateToTime(details.incidentFaultTimeStamp) : undefined,
      );
      this.fixedDate.set(
        details?.incidentFixedTimeStamp ? new Date(details.incidentFixedTimeStamp) : undefined,
      );
      this.fixedTimeStamp.set(
        details?.incidentFixedTimeStamp ? isoDateToTime(details.incidentFixedTimeStamp) : undefined,
      );
      this.impactName.set(details?.incidentImpactName ?? undefined);
      this.incidentStateName.set(details?.incidentStateName ?? 'pending');
      this.incidentTitle.set(details?.incidentTitle ?? undefined);
      this.hasInterruptions.set(details?.hasInterruptions === 1 ? 'yes' : 'no');
      this.faultEffectId.set(details?.faultEffectId ?? undefined);
    });
  });

  _initOptions = effect(() => {
    const tenantId = this.tenantId();
    const accessCode = this.accessCode();

    if (tenantId && accessCode) {
      this.detailsService.loadDataFragment(DataFragments.faulteffects, accessCode, tenantId);
    }
  });

  _incidentChanged = effect(() => {
    const updated = this.incidentService.updateResult();
    if (updated) {
      untracked(() => {
        this.incidentService.getIncidentDetails(
          this.accessCode() as string,
          this.tenantId() as string,
        );
      });
    }
  });

  _onRouteChanged = effect(() => {
    this.activatedRoute.snapshot.url.toString();
    const accessCode = this.activatedRoute.snapshot.paramMap.get('accessCode');
    if (this.mode() === 'create') {
      untracked(() => {
        this.incidentService.getIncidentDetails(accessCode as string);
      });
    }
  });

  _detailData = effect(() => {
    const faultEffectId = this.faultEffectId();
    const faultEffectOptions =
      this.detailsService.fragmentToSignal[DataFragments.faulteffects].dataSignal();

    if (faultEffectId && faultEffectOptions) {
      const faultEffectKey = faultEffectOptions.find(elem => {
        return elem.key == this.faultEffectId();
      });

      untracked(() => {
        this.faultEffectName.set(faultEffectKey?.value ?? undefined);
      });
    }
  });
}
